// src/App.js
import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import axios from 'axios';

import { trackEvent } from "./utils/analytics";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ScrollToTop from "./components/ScrollToTop";

// Keep critical pages normal imports
import Home from "./Home";
import Shop from "./Shop";
import ProductPreview from './ProductPreview';
import Cart from './Cart';

// Lazy imports AFTER all imports
const CheckoutPage = lazy(() => import('./CheckoutPage'));
const Contact = lazy(() => import('./Contact'));
const About = lazy(() => import('./About'));
const TrackingPage = lazy(() => import('./TrackingPage'));
const ReturnDetails = lazy(() => import('./ReturnDetails'));
const CollectionPage = lazy(() => import("./CollectionPage"));

const SizeGuideLanding = lazy(() => import('./size-guide/LandingPage'));
const StillSizer = lazy(() => import('./size-guide/Shushfit'));
const Brand = lazy(() => import('./size-guide/Brand'));
const ManualEntry = lazy(() => import('./size-guide/ManualEntry'));
const Width = lazy(() => import('./size-guide/Width'));
const BrandSizePage = lazy(() => import('./size-guide/NextPage'));
const FinalSize = lazy(() => import('./size-guide/Finalsize'));

const ReturnRefundPolicy = lazy(() => import('./ReturnRefundPolicy'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const ShippingPolicy = lazy(() => import('./ShippingPolicy'));
const TermsConditions = lazy(() => import('./TermsConditions'));

const ReviewPage = lazy(() => import('./pages/ReviewPage'));

const WorkHeels = lazy(() => import('./WorkHeels'));
const AccessibleLuxury = lazy(() => import('./AccessibleLuxury'));
const Slingback = lazy(() => import('./Slingbacks'));
const Pumps = lazy(() => import('./Pumps'));
const BlackHeels = lazy(() => import('./BlackHeels'));

const BlogPage = lazy(() => import('./BlogPage'));
const BlogAllPage = lazy(() => import('./BlogAll'));

const BestWorkHeels = lazy(() => import('./blogs/BestWorkHeels'));
const HeelHeightDailyWear = lazy(() => import('./blogs/HeelHeightDailyWear'));
const StyleSlingback = lazy(() => import('./blogs/StyleSlingback'));
const BlockvsFlared = lazy(() => import('./blogs/BlockvsFlared'));
const OneExceptionalShoe = lazy(() => import('./blogs/OneExceptionalShoe'));

const AgencyLogin = lazy(() => import("./pages/AgencyLogin"));
const AgencyDashboard = lazy(() => import("./pages/AgencyDashboard"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));

const NotFound = lazy(() => import('./NotFound'));


function App() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  // Fetch products from backend
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/products`)
      .then((res) => setProducts(res.data))
      .catch((err) => console.error("Failed to fetch products:", err));
  }, []);

  // Add product to cart
  const addToCart = (product) => {

    setCart((prevCart) => {

      const existing = prevCart.find(
        (item) =>
          item._id === product._id &&
          item.selectedSize === product.selectedSize
      );

      const updatedCart = existing
        ? prevCart.map((item) =>
            item._id === product._id &&
            item.selectedSize === product.selectedSize
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        : [...prevCart, { ...product, quantity: 1 }];

      trackEvent({
        event: "add_to_cart",
        ecommerce: {
          currency: "INR",
          value: product.price,
          items: [
            {
              item_id: product._id,
              item_name: product.name,
              item_category: product.category,
              item_brand: product.brand,
              item_variant: product.selectedSize,
              price: product.price,
              quantity: 1
            }
          ]
        }
      });

      return updatedCart;
    });
  };

  // Remove product from cart
  const removeFromCart = (productId) => {
    setCart((prevCart) => prevCart.filter((item) => item._id !== productId));
  };

  // Update quantity in cart
  const updateQuantity = (productId, quantity) => {
    setCart((prevCart) =>
      prevCart.map((item) =>
        item._id === productId
          ? { ...item, quantity: Math.max(1, quantity) }
          : item
      )
    );
  };

  return (
    <>
    <Router>
    <AnalyticsTracker />
    <ScrollToTop />
    <Suspense fallback={<div>Loading...</div>}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route
          path="/shop"
          element={
            <Shop
              products={products}
              cart={cart}
              setCart={setCart}
              addToCart={addToCart}
            />
          }
        />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/tracking" element={<TrackingPage />} />
        <Route path="/tracking/returns/:id" element={<ReturnDetails />} />
        <Route path="/size-guide" element={<SizeGuideLanding />} />
        <Route path="/virtual-fit" element={<StillSizer />} />
        <Route path="/size-guide/brand" element={<Brand />} />
        <Route path="/size-guide/brand-size" element={<BrandSizePage />} />
        <Route path="/size-guide/manual-entry" element={<ManualEntry />} />
        <Route path="/size-guide/width" element={<Width />} />
        <Route path="/size-guide/final" element={<FinalSize />} />
        <Route path="/return-policy" element={<ReturnRefundPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/terms-and-conditions-policy" element={<TermsConditions />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/work-heels" element={<WorkHeels />} />
        <Route path="/accessible-luxury-footwear" element={<AccessibleLuxury />} />
        <Route path="/slingbacks" element={<Slingback />} />
        <Route path="/pumps" element={<Pumps />} />
        <Route path="/black-heels" element={<BlackHeels />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/best-heels-for-work-india" element={<BestWorkHeels />} />
        <Route path="/blog/best-heel-height-for-india" element={<HeelHeightDailyWear />} />
        <Route path="/blog/how-to-style-slingback-heels" element={<StyleSlingback />} />
        <Route path="/blog/flared-heels-vs-block-heels" element={<BlockvsFlared />} />
        <Route path="/blog/that-one-exceptional-shoe" element={<OneExceptionalShoe />} />
        <Route path="/blog/all" element={<BlogAllPage />} />

        <Route
          path="/internal/agency-portal-72hj"
          element={<AgencyLogin />}
        />

        <Route
          path="/agency-dashboard"
          element={
            <ProtectedRoute>
              <AgencyDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/collection/:collectionName"
          element={
            <CollectionPage
              products={products}
              cart={cart}
              setCart={setCart}
              addToCart={addToCart}
            />
          }
        />

        <Route path="*" element={<NotFound/>} />
        <Route
          path="/product/:id"
          element={
            <ProductPreview
              products={products}
              cart={cart}
              addToCart={addToCart}
              setCartItems={setCart}
              key={window.location.pathname}
            />
          }
        />

        <Route
          path="/checkout"
          element={
            <CheckoutPage
              cart={cart}
              removeFromCart={removeFromCart}
              updateQuantity={updateQuantity}
            />
          }
        />
        <Route
          path="/cart"
          element={
            <Cart
              cart={cart}
              removeFromCart={removeFromCart}
              updateQuantity={updateQuantity}
            />
          }
        />
      </Routes>
    </Suspense>
    </Router>
    </>
  );
}

export default App;
